import { PerfAggregator } from './aggregator';
import { getPerfEventReporter } from './reporter';
import { PerfLabel, PerfWindowSnapshot, VideoFpsSource } from './types';

export type { PerfLabel, PerfWindowSnapshot, VideoFpsSource, WindowKind } from './types';

let aggregator: PerfAggregator | null = null;
let initialized = false;

type WindowReportHandler = (snapshot: PerfWindowSnapshot) => void;
const reportHandlers = new Set<WindowReportHandler>();

const emitReport = (snapshot: PerfWindowSnapshot | null): void => {
  if (!snapshot) return;
  reportHandlers.forEach((h) => h(snapshot));
};

/**
 * 앱 시작 시 1회 호출. SSR/미지원 환경에서 graceful no-op.
 * 첫 mirror window를 자동 시작한다. snapshot 중계는 store.ts가 담당.
 */
export const initPerfMonitor = (): void => {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  aggregator = new PerfAggregator();
  aggregator.start();
  aggregator.transitionTo('mirror', `mirror:${Date.now()}`);
};

/**
 * 모니터를 완전히 정지하고 초기화 상태를 리셋한다.
 * HMR 또는 테스트 환경에서 중복 실행을 방지하기 위해 사용한다.
 */
export const destroyPerfMonitor = (): void => {
  aggregator?.stop();
  aggregator = null;
  initialized = false;
};

/**
 * store/debug UI가 aggregator에 직접 접근하기 위한 훅.
 */
export const getAggregator = (): PerfAggregator | null => aggregator;

/**
 * Window가 종료될 때마다 호출되는 리포트 핸들러 등록.
 * unsubscribe 함수를 반환한다.
 */
export const onWindowReport = (handler: WindowReportHandler): (() => void) => {
  reportHandlers.add(handler);
  return () => {
    reportHandlers.delete(handler);
  };
};

/**
 * 매치 시작 시 호출. 진행 중이던 mirror window를 종료하고 match window로 전환.
 * 이전 mirror window snapshot이 onWindowReport 핸들러로 emit된다.
 */
export const startMatchWindow = (matchId: string): void => {
  if (!aggregator) return;
  const ended = aggregator.transitionTo('match', `match:${matchId}`, matchId);
  emitReport(ended);
  getPerfEventReporter()?.onMatchStart();
};

/**
 * 매치 종료 시 호출. 진행 중이던 match window를 종료하고 mirror window로 전환.
 * 이전 match window snapshot이 onWindowReport 핸들러로 emit된다.
 */
export const startMirrorWindow = (): void => {
  if (!aggregator) return;
  const ended = aggregator.transitionTo('mirror', `mirror:${Date.now()}`);
  emitReport(ended);
};

/**
 * 동기 함수 측정. fn의 실행 시간(ms)을 라벨에 누적한다.
 * 활성 window에만 기록된다 (어느 시점이든 최대 1개).
 */
export const measure = <T>(label: PerfLabel, fn: () => T): T => {
  if (!aggregator) return fn();
  const start = performance.now();
  try {
    return fn();
  } finally {
    const duration = performance.now() - start;
    aggregator.recordToTracer(label, duration);
  }
};

/**
 * 비동기 함수 측정. await 포함 전체 wall-clock 시간을 측정.
 * 메인스레드 점유 시간이 아니라 사용자가 인지하는 latency 기준이라는 점에 주의.
 */
export const measureAsync = async <T>(label: PerfLabel, fn: () => Promise<T>): Promise<T> => {
  if (!aggregator) return fn();
  const start = performance.now();
  try {
    return await fn();
  } finally {
    const duration = performance.now() - start;
    aggregator.recordToTracer(label, duration);
  }
};

/**
 * 시작 시점만 잡고, 종료를 callee가 결정하는 패턴.
 * await가 필수가 아닌 비동기 흐름에 사용.
 *
 * @example
 *   const end = mark('face.detect.mediapipe');
 *   someAsyncOp((result) => { end(); ... });
 */
export const mark = (label: PerfLabel): (() => void) => {
  if (!aggregator) return () => {};
  const start = performance.now();
  const agg = aggregator;
  return () => {
    const duration = performance.now() - start;
    agg.recordToTracer(label, duration);
  };
};

/**
 * 파이프 렌더 완료 시 호출하여 render FPS를 기록한다.
 */
export const recordVideoFrame = (source: VideoFpsSource): void => {
  aggregator?.recordVideoFrame(source);
};

/**
 * RTCPeerConnection.getStats()에서 얻은 소스 FPS를 보고한다.
 * local: RTCMediaSourceStats.framesPerSecond
 * peer: RTCInboundRtpStreamStats.framesPerSecond
 */
export const reportSourceFps = (source: 'local' | 'peer', fps: number): void => {
  aggregator?.reportSourceFps(source, fps);
};

/**
 * RTCOutboundRtpStreamStats.framesPerSecond — 인코딩/전송 FPS를 보고한다.
 * local 비디오 전용. 파이프 → WebRTC 인코더 → 네트워크 전송 단계의 실제 FPS.
 */
export const reportEncodeFps = (fps: number): void => {
  aggregator?.reportEncodeFps(fps);
};

/**
 * 현재 활성 window의 snapshot을 즉시 가져온다.
 */
export const getCurrentSnapshot = (): PerfWindowSnapshot | null => {
  return aggregator?.getCurrentSnapshot() ?? null;
};

/**
 * Effect vision worker 구동 여부를 성능 이벤트로그에 실을 세션 상태로 기록한다.
 * EffectRendererPipe 초기화 성공 시 호출된다.
 */
export const setEffectWorkerRuntime = (isRunning: boolean | null): void => {
  getPerfEventReporter()?.setEffectWorkerRuntime(isRunning);
};
